import type { ZodSchema } from 'zod'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function notifyUnauthorized(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:unauthorized'))
  }
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// WP-polish (2026-09-23): plain fetch() has no timeout of its own. A
// request that hangs forever -- e.g. a load balancer/proxy that accepted
// the connection but never got an answer from a backend mid-restart --
// used to leave the page stuck on its loading state indefinitely, since
// nothing ever settled to trigger react-query's error/retry handling.
// This bounds every request so a hang becomes a normal, retryable error
// instead.
const REQUEST_TIMEOUT_MS = 20_000

async function fetchWithTimeout(
  path: string,
  init: RequestInit,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(path, { ...init, signal: controller.signal })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, 'The server took too long to respond. Please try again.')
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Read the cc_csrf cookie (non-HttpOnly) so mutating requests can echo it
 * back as the X-CSRF-Token header — closes the SameSite=Lax residual gap.
 */
function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)cc_csrf=([^;]+)/)
  return match && match[1] ? decodeURIComponent(match[1]) : null
}

function withCsrf(init?: RequestInit): RequestInit | undefined {
  const method = (init?.method || 'GET').toUpperCase()
  if (!MUTATING_METHODS.has(method)) return init
  const token = readCsrfToken()
  if (!token) return init
  return {
    ...init,
    headers: {
      ...(init?.headers || {}),
      'X-CSRF-Token': token,
    },
  }
}

/**
 * Typed fetch wrapper. Validates every response against a zod schema so a
 * silent backend/frontend drift becomes a loud parse error in development.
 *
 * Mutations should pass `init.method` and `init.body` (already JSON-stringified).
 */
export async function apiFetch<T>(
  path: string,
  schema: ZodSchema<T>,
  init?: RequestInit,
): Promise<T> {
  const scoped = withCsrf(init)
  const response = await fetchWithTimeout(path, {
    ...scoped,
    headers: {
      Accept: 'application/json',
      ...(scoped?.body ? { 'Content-Type': 'application/json' } : {}),
      ...scoped?.headers,
    },
  })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (body && typeof body.detail === 'string') {
        detail = body.detail
      }
    } catch {
      // Body wasn't JSON; keep statusText.
    }
    if (response.status === 401) notifyUnauthorized()
    throw new ApiError(response.status, detail)
  }
  const data = await response.json()
  return schema.parse(data)
}

/**
 * Plain JSON fetch — used by mutations whose response shape we don't care
 * about beyond the HTTP status.
 */
/** Longest a slow AI-backed action may take (4 Oct 2026): "Generate
 *  timeline" reads the uploaded documents with the AI, which can take
 *  minutes (backend timeout 300s). The 20s default made the page show
 *  "took too long" while the draft was still being created. */
export const SLOW_AI_REQUEST_TIMEOUT_MS = 330_000

export async function apiSend(
  path: string,
  init: RequestInit,
  opts?: { timeoutMs?: number },
): Promise<void> {
  const scoped = withCsrf(init) ?? init
  const response = await fetchWithTimeout(path, {
    ...scoped,
    headers: {
      Accept: 'application/json',
      ...(scoped.body ? { 'Content-Type': 'application/json' } : {}),
      ...scoped.headers,
    },
  }, opts?.timeoutMs)
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      if (body && typeof body.detail === 'string') {
        detail = body.detail
      }
    } catch {
      // ignore
    }
    if (response.status === 401) notifyUnauthorized()
    throw new ApiError(response.status, detail)
  }
}
